// src/components/store/CartDrawer.jsx
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import "../../static/storecss/CartDrawer.css";

export default function CartDrawer() {
  const {
    items,
    total,
    count,
    drawerOpen,
    closeDrawer,
    updateQty,
    removeItem,
  } = useCart();

  const panelRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && drawerOpen) closeDrawer();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen, closeDrawer]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        if (e.target.closest(".nav-cart-link")) return;
        closeDrawer();
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [drawerOpen, closeDrawer]);

  return (
    <div
      ref={panelRef}
      className={`cart-panel ${drawerOpen ? "is-open" : ""}`}
      role="dialog"
      aria-label="Shopping cart"
      aria-hidden={!drawerOpen}
    >
      <div className="cart-panel-header">
        <span className="cart-panel-title">
          <i className="fa-solid fa-shopping-cart" aria-hidden="true" />
          Your cart
          {count > 0 && (
            <span className="cart-panel-count">
              {count} {count === 1 ? "item" : "items"}
            </span>
          )}
        </span>
        <button
          type="button"
          className="cart-panel-close"
          onClick={closeDrawer}
          aria-label="Close cart"
        >
          <i className="fa-solid fa-xmark" />
        </button>
      </div>

      <div className="cart-panel-body">
        {items.length === 0 ? (
          <div className="cart-panel-empty">
            <i className="fa-solid fa-cart-shopping" />
            <p>Your cart is empty.</p>
            <Link
              to="/store"
              className="cart-panel-empty-btn"
              onClick={closeDrawer}
            >
              Browse store
            </Link>
          </div>
        ) : (
          <ul className="cart-panel-list">
            {items.map((it) => (
              <li key={it.id} className="cart-panel-item">
                <Link
                  to={`/store/product/${it.slug}`}
                  className="cart-panel-thumb"
                  onClick={closeDrawer}
                >
                  {it.primary_image ? (
                    <img src={it.primary_image} alt={it.name} loading="lazy" />
                  ) : (
                    <span className="cart-panel-thumb-ph">
                      <i className="fa-solid fa-image" />
                    </span>
                  )}
                </Link>

                <div className="cart-panel-info">
                  <Link
                    to={`/store/product/${it.slug}`}
                    className="cart-panel-name"
                    onClick={closeDrawer}
                  >
                    {it.name}
                  </Link>
                  <span className="cart-panel-price">
                    KES {Number(it.price).toLocaleString()}
                  </span>

                  <div className="cart-panel-qty" role="group" aria-label="Quantity">
                    <button
                      type="button"
                      onClick={() => updateQty(it.id, it.quantity - 1)}
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <span>{it.quantity}</span>
                    <button
                      type="button"
                      onClick={() => updateQty(it.id, it.quantity + 1)}
                      aria-label="Increase quantity"
                      disabled={it.quantity >= it.stock}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="cart-panel-end">
                  <span className="cart-panel-subtotal">
                    KES{" "}
                    {Number(
                      it.subtotal ?? it.price * it.quantity
                    ).toLocaleString()}
                  </span>
                  <button
                    type="button"
                    className="cart-panel-remove"
                    onClick={() => removeItem(it.id)}
                    aria-label={`Remove ${it.name}`}
                  >
                    <i className="fa-solid fa-trash" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {items.length > 0 && (
        <div className="cart-panel-footer">
          <div className="cart-panel-total">
            <span>Subtotal</span>
            <strong>KES {Number(total).toLocaleString()}</strong>
          </div>
          <Link
            to="/checkout"
            className="cart-panel-btn cart-panel-btn--primary cart-panel-btn--block"
            onClick={closeDrawer}
          >
            Proceed to checkout <i className="fa-solid fa-arrow-right" />
          </Link>
        </div>
      )}
    </div>
  );
}